import React, { useCallback, useEffect, useState } from 'react'
import { FormControl, Grid, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import languagesData from '../../../../src/data/languages.json'
import MyMultiSelect from '../../../components/MyMultiSelect'
import {
  eyesOptions,
  hairOptions,
  nationalitiesOptions,
  intimateOptions,
  breastsOptions,
  ethnicityOptions,
  genderOptions,
  orientationOptions,
} from './options/optionsProfile'
import MySelect from '../../../components/MySelect'
import MyInput from '../../../components/MyInput'
import MyCheckbox from '../../../components/MyCheckbox'
import {
  FaCut,
  FaEye,
  FaFlag,
  FaGlobe,
  FaRegDotCircle,
  FaTransgender,
  FaUser,
} from 'react-icons/fa'
import { GiBodyHeight, GiUnderwearShorts, GiWeight } from 'react-icons/gi'
import { IoIosHand, IoMdResize } from 'react-icons/io'
import { HiSpeakerphone } from 'react-icons/hi'
import { getUserProfile } from '../../../../services/profileService'
import { GoAlertFill } from 'react-icons/go'
import CookieManager from '../../../lib/CookiesManager'

interface UserProfileFormProps {
  formData: any
  handleFieldChange: (key: string, value: any) => void
  isNewProfile: boolean
}

const UserProfileForm: React.FC<UserProfileFormProps> = ({
  formData: initialFormData,
  handleFieldChange: propHandleFieldChange,
  isNewProfile,
}) => {
  const { t } = useTranslation()
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})
  const userId = Number(CookieManager.getUserId()) || 0

  const [formData, setFormData] = useState<{ [key: string]: any }>(() => {
    return {
      ...initialFormData,
      massageOnly: initialFormData.massageOnly ?? false,
    }
  })

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!isNewProfile) {
        try {
          const profileData = await getUserProfile()
          if (Object.keys(profileData).length > 0) {
            const processedData = Object.entries(profileData).reduce(
              (acc, [key, value]) => {
                if (['size', 'weight', 'penisSize'].includes(key)) {
                  acc[key] = value !== null ? Number(value) : ''
                } else {
                  acc[key] = value
                }
                return acc
              },
              {} as Record<string, any>,
            )
            setFormData(prevData => ({
              ...prevData,
              ...processedData,
            }))
          }
        } catch (error: any) {
          Sentry.captureException(error, {
            extra: {
              action: 'fetchUserProfile',
              userId,
              timestamp: new Date().toISOString(),
            },
            tags: {
              type: 'api_error',
              method: 'fetchUserProfile',
            },
          })
          setError('Error fetching user profile')
        }
      }
    }

    fetchUserProfile()
  }, [isNewProfile])

  const validateNumberField = (
    value: string,
    min: number,
    max: number,
    fieldKey: string,
  ) => {
    const numValue = Number(value)

    if (numValue < min || numValue > max) {
      switch (fieldKey) {
        case 'weight':
          return 'create-ad.invalid-weight'
        case 'size':
          return 'create-ad.invalid-size'
        case 'penisSize':
          return 'create-ad.invalid-penis-size'
        default:
          return 'create-ad.number-between'
      }
    }
    return ''
  }

  const handleFieldChange = useCallback(
    (key: string, value: any) => {
      propHandleFieldChange(key, value)
      setFieldErrors(prev => ({ ...prev, [key]: '' }))
    },
    [propHandleFieldChange],
  )

  const handleNumberChange = (
    key: string,
    value: string,
    min: number,
    max: number,
  ) => {
    const error = validateNumberField(value, min, max, key)
    setFieldErrors(prev => ({ ...prev, [key]: error }))
    if (!error) {
      handleFieldChange(key, Number(value))
    }
  }

  const handleReset = useCallback((key: string) => {
    setFormData(prevFormData => {
      const newFormData = { ...prevFormData }
      newFormData[key] = ''
      localStorage.setItem('myProfile', JSON.stringify(newFormData))
      return newFormData
    })
  }, [])

  useEffect(() => {
    setFormData(prevData => ({
      ...prevData,
      ...initialFormData,
      massageOnly: initialFormData.massageOnly ?? prevData.massageOnly,
    }))
  }, [initialFormData])

  const languageOptions = Object.entries(languagesData.languages).map(
    ([id]) => ({
      value: id,
      label: t(`languages.${id}`),
    }),
  )

  const renderField = (field: any) => {
    const fieldKey = field.label
    let fieldValue = formData[fieldKey] || ''

    if (
      (fieldKey === 'penisSize' && formData.gender === 'woman') ||
      (fieldKey === 'braCup' && formData.gender === 'man')
    ) {
      return null
    }

    switch (field.type) {
      case 'select':
        if (fieldKey === 'nationality') {
          return (
            <FormControl fullWidth>
              <MySelect
                value={fieldValue}
                label={t(field.labelKey || `create-ad.${field.label}`)}
                readOnly={false}
                className='h-[40px]'
                showNonMatchingOptions={true}
                onChange={value => handleFieldChange(fieldKey, value)}
                options={field.options.map((option: string) => ({
                  value: option,
                  label:
                    fieldKey === 'nationality'
                      ? t(`nationalities.${option}`)
                      : t(`create-ad.${option}`),
                }))}
                icon={field.icon}
                error={fieldErrors[fieldKey]}
                initialValue={fieldValue}
                handleReset={() => handleReset(fieldKey)}
                required={field.required}
              />
            </FormControl>
          )
        }

        return (
          <FormControl fullWidth>
            <MySelect
              value={fieldValue}
              label={t(field.translationKey || field.labelKey)}
              readOnly={true}
              className='h-[40px]'
              onChange={value => handleFieldChange(fieldKey, value)}
              showNonMatchingOptions={true}
              options={field.options.map((option: string) => ({
                value: option,
                label: t(`create-ad.${option}`),
              }))}
              icon={field.icon}
              error={fieldErrors[fieldKey]}
              initialValue={fieldValue}
              handleReset={() => handleReset(fieldKey)}
              required={field.required}
            />
          </FormControl>
        )

      case 'input':
        return (
          <FormControl fullWidth>
            <MyInput
              label={t(field.translationKey || `create-ad.${field.label}`)}
              type='number'
              value={fieldValue.toString()}
              onChange={value =>
                handleNumberChange(fieldKey, value, field.min, field.max)
              }
              error={fieldErrors[fieldKey]}
              icon={field.icon}
              min={field.min}
              max={field.max}
              required={field.required}
              isInvalid={!!fieldErrors[fieldKey]}
              helperText={
                fieldValue && fieldErrors[fieldKey]
                  ? t(fieldErrors[fieldKey], {
                      min: field.min,
                      max: field.max,
                      defaultValue: `${field.min}-${field.max}`,
                    })
                  : ''
              }
            />
          </FormControl>
        )

      case 'multiselect':
        return (
          <MyMultiSelect
            label={t(field.labelKey || `create-ad.${field.label}`)}
            options={languageOptions}
            value={fieldValue || []}
            onChange={newValue => handleFieldChange(fieldKey, newValue)}
            error={fieldErrors[fieldKey]}
            maxVisibleOptions={10}
            readOnly={false}
            icon={<HiSpeakerphone />}
            required={field.required}
          />
        )

      case 'checkbox':
        if (field.label === 'checkboxGroup') {
          return (
            <FormControl component='fieldset' style={{ width: '100%' }}>
              <Grid container spacing={2}>
                {field.options.map(
                  (option: {
                    key: string
                    translationKey: string
                    required?: boolean
                  }) => (
                    <Grid item xs={3} key={option.key}>
                      <Box className='w-full bg-grisclair rounded-lg cursor-pointer'>
                        <MyCheckbox
                          name={option.key}
                          label={t(option.translationKey)}
                          checked={formData[option.key] || false}
                          required={option.required}
                          onChange={checked =>
                            handleFieldChange(option.key, checked)
                          }
                        />
                      </Box>
                    </Grid>
                  ),
                )}
              </Grid>
            </FormControl>
          )
        } else {
          return (
            <FormControl fullWidth>
              <Box className='w-full bg-grisclair rounded-lg cursor-pointer'>
                <MyCheckbox
                  name={fieldKey}
                  label={
                    <div>
                      {t(field.translationKey)}
                      <p className='text-xs'>
                        {t('professional-massage-description')}
                      </p>
                    </div>
                  }
                  checked={fieldValue || false}
                  required={field.required}
                  onChange={checked => handleFieldChange(fieldKey, checked)}
                  isMassageOnly={fieldKey === 'massageOnly'}
                />
              </Box>
            </FormControl>
          )
        }

      default:
        return null
    }
  }

  const fields = [
    {
      type: 'checkbox',
      label: 'massageOnly',
      translationKey: 'create-ad.professional-massage-no-sex',
      icon: <IoIosHand />,
      required: false,
    },
    {
      type: 'select',
      label: 'gender',
      translationKey: 'create-ad.gender',
      options: genderOptions.map(o => o.value),
      icon: <FaUser />,
      required: true,
    },
    {
      type: 'select',
      label: 'orientation',
      translationKey: 'create-ad.sexual-orientation',
      options: orientationOptions.map(o => o.value),
      icon: <FaTransgender />,
      required: true,
    },
    {
      type: 'select',
      label: 'nationality',
      translationKey: 'nationalities',
      options: nationalitiesOptions.map(o => o.value),
      icon: <FaFlag />,
      required: true,
    },
    {
      type: 'select',
      label: 'ethnicity',
      translationKey: 'create-ad.ethnicity',
      options: ethnicityOptions.map(o => o.value),
      icon: <FaGlobe />,
      required: true,
    },
    {
      type: 'input',
      label: 'size',
      translationKey: 'create-ad.size',
      icon: <GiBodyHeight />,
      min: 140,
      max: 220,
      required: true,
    },
    {
      type: 'input',
      label: 'weight',
      icon: <GiWeight />,
      min: 40,
      max: 120,
      required: true,
    },
    {
      type: 'select',
      label: 'eyeColor',
      translationKey: 'create-ad.eyes',
      options: eyesOptions.map(o => o.value),
      icon: <FaEye />,
      required: false,
    },
    {
      type: 'select',
      label: 'hairColor',
      translationKey: 'create-ad.hair',
      options: hairOptions.map(o => o.value),
      icon: <FaCut />,
      required: false,
    },
    {
      type: 'select',
      label: 'intimateHairCut',
      translationKey: 'create-ad.intimate-haircut',
      options: intimateOptions.map(o => o.value),
      icon: <GiUnderwearShorts />,
      required: false,
    },
    {
      type: 'select',
      label: 'braCup',
      translationKey: 'create-ad.breasts',
      options: breastsOptions.map(o => o.value),
      icon: <FaRegDotCircle />,
      required: false,
    },
    {
      type: 'input',
      label: 'penisSize',
      icon: <IoMdResize />,
      min: 1,
      max: 40,
      required: false,
    },
    {
      type: 'multiselect',
      label: 'languages',
      required: true,
    },
    {
      type: 'checkbox',
      label: 'checkboxGroup',
      translationKey: 'create-ad.checkbox',
      options: [
        { key: 'smoker', translationKey: 'create-ad.smoker' },
        { key: 'tattoo', translationKey: 'create-ad.tattoos' },
        { key: 'piercing', translationKey: 'create-ad.piercings' },
        { key: 'alcohol', translationKey: 'create-ad.alcohol' },
        {
          key: 'consentConditions',
          translationKey: 'create-ad.consent-conditions',
          required: true,
        },
      ],
      required: false,
    },
  ]

  const renderFields = () => {
    const oneThirdFields: any[] = []
    const oneQuarterFields: any[] = []
    const fullWidthFields: any[] = []
    let consentConditionsField: any = null
    let massageOnlyField: any = null

    fields.forEach(field => {
      if (
        (field.label === 'penisSize' && formData.gender === 'woman') ||
        (field.label === 'braCup' && formData.gender === 'man')
      ) {
        return
      }

      const renderedField = renderField(field)

      if (field.label === 'massageOnly') {
        massageOnlyField = (
          <div key='massageOnly' className='w-full mb-3'>
            {renderedField}
          </div>
        )
      } else if (field.type === 'checkbox' && field.label === 'checkboxGroup') {
        const checkboxes = renderedField
          ? renderedField.props.children.props.children
          : null
        checkboxes.forEach((checkbox: any) => {
          if (checkbox.key === 'consentConditions') {
            consentConditionsField = (
              <div key='consentConditions' className='w-full mb-4'>
                <Box className='w-full bg-grisclair rounded-lg cursor-pointer'>
                  {checkbox.props.children}
                </Box>
              </div>
            )
          } else {
            oneQuarterFields.push(
              <div key={checkbox.key} className='w-full sm:w-1/4 px-2 mb-4'>
                <Box className='w-full bg-grisclair rounded-lg cursor-pointer'>
                  {checkbox.props.children}
                </Box>
              </div>,
            )
          }
        })
      } else if (field.type === 'multiselect') {
        fullWidthFields.push(<div className='mt-2'>{renderedField}</div>)
      } else {
        oneThirdFields.push(renderedField)
      }
    })

    return (
      <div className='text-sm'>
        {massageOnlyField}
        <div className='flex flex-wrap -mx-2'>
          {oneThirdFields.map((field, index) => (
            <div key={index} className='w-full sm:w-1/3 px-2 mb-3 mt-3'>
              {field}
            </div>
          ))}
        </div>
        <div className='flex flex-wrap -mx-2 mt-3'>{oneQuarterFields}</div>
        {fullWidthFields.map((field, index) => (
          <div key={index} className='w-full mb-4'>
            {field}
          </div>
        ))}
        {consentConditionsField}
      </div>
    )
  }

  return (
    <div className='user-profile-form'>
      <h2 className='w-full bg-anthracite flex text-white mb-4 py-2 rounded-lg px-3 text-sm'>
        <GoAlertFill className='text-xl text-redpink mr-2' />
        {t('create-ad.profile-notice')}
      </h2>
      {renderFields()}
    </div>
  )
}

export default UserProfileForm
